import {
  ZERO, add, clampPos, distribute, maxM, minM, share, sub, type Money,
} from './money.js';
import { conservativeIncome } from './available.js';
import { monthlyObligations } from './instances.js';
import { creditState } from './credit.js';
import { MODE_SPLIT } from './mode.js';
import type { Account, CreditTerms, Goal, IncomeStream, Obligation } from './types.js';

/** Насколько агрессивно пользователь идёт к целям. */
export type PlanMode = 'live_now' | 'balanced' | 'goals_first';

export interface PlanInput {
  incomeStreams: IncomeStream[];
  obligations: Obligation[];
  /** Медиана обычных трат на повседневные нужды помимо обязательств. */
  needBaseline: Money;
  goals: Goal[];
  accounts: Account[];
  creditTerms: CreditTerms[];
  /** Месячные расходы на повседневные нужды — для порогов подушки. */
  mode: PlanMode;
  today: import('./date.js').PlainDate;
}

export interface PlanShares { need: number; want: number; save: number }

export interface PlanResult {
  income: Money;
  /** Ниже этого «Могу» опуститься не может: обязательства + базовая жизнь. */
  needFloor: Money;
  /** Обязательный минимум «Коплю»: долги, резервы, подушка до 1 месяца. */
  mustFloor: Money;
  /** Что осталось на распределение между «Хочу» и «Коплю». */
  surplus: Money;
  shares: PlanShares;
  amounts: { need: Money; want: Money; save: Money };
  /** Свободных денег почти нет — предлагать проценты бессмысленно. */
  trapped: boolean;
  /** Как получились доли — для объяснения пользователю. */
  rationale: string[];
}

/*
  Доли берутся из общей таблицы (`mode.ts`).

  Здесь была своя копия, и она разошлась с раскладкой: у `goals_first`
  месячный план делил 25/75, а раскладка — 30/70. Человек видел два разных
  числа на соседних экранах при одном и том же выборе.
*/

/** Порог, при котором свободных денег считается «почти нет». */
const TRAP_THRESHOLD = 0.05;

/**
 * Рекомендованное распределение Могу / Хочу / Коплю (PRD §2.2).
 *
 * Продукт **не навязывает 50/30/20**: он считает реальность пользователя.
 * Классические проценты предполагают, что обязательные расходы укладываются
 * в половину дохода, а при ипотеке с ребёнком это не так — и человек, которому
 * приложение говорит «тратьте на нужное не больше 50%», просто закрывает его.
 */
export function computePlan(input: PlanInput): PlanResult {
  const rationale: string[] = [];

  const income = add(...input.incomeStreams.filter((s) => s.isActive).map(conservativeIncome));

  // Шаг 1. Пол по обязательствам: их нельзя не заплатить.
  //
  //    Считается по календарю наступлений, а не сложением всех активных
  //    обязательств: годовая страховка 60 000 ₽ входит в свой месяц целиком
  //    и не превращается в ежемесячный платёж.
  const obligationsMonthly = monthlyObligations(input.obligations, input.today);
  const needFloor = add(obligationsMonthly, input.needBaseline);
  rationale.push(
    `Обязательные платежи ${fmt(obligationsMonthly)} плюс базовая жизнь ${fmt(input.needBaseline)} — ниже этого «Могу» не опускается`,
  );

  // Шаг 2. Пол по «Коплю»: минимальные платежи по долгам, резервы под будущие
  //        счёта и подушка до первого порога — это не выбор, а безопасность.
  const termsById = new Map(input.creditTerms.map((t) => [t.accountId, t]));
  // Неизвестный минимальный платёж в пол не входит.
  //
  // Прежде он всегда был известен, потому что подставлялся кодом: пять
  // процентов долга по выписке. У человека с долгом 152 000 ₽ это 7 599 ₽,
  // которые молча уходили из «Свободные траты» — при том что настоящий процент его
  // банка никто не спрашивал.
  const minDebtPayments = add(
    ...input.accounts
      .filter((a) => a.kind === 'credit_card' && termsById.has(a.id))
      .map((a) => creditState(a, termsById.get(a.id)!, input.today).minPayment ?? ZERO),
  );
  const sinkingFunds = add(
    ...input.goals
      .filter((g) => g.status === 'active' && g.kind === 'sinking_fund')
      .map((g) => g.monthlyContribution),
  );
  const monthlyNeed = needFloor;
  /*
    Подушка входит в обязательный минимум **месячным взносом**, а не всем
    разрывом до порога.

    Раньше сюда попадал весь разрыв: человеку с обязательствами на 125 000 ₽
    и пустой подушкой приложение записывало в обязательный минимум 170 000 ₽ —
    месячный запас целиком. Столько не остаётся ни у кого, остаток обнулялся,
    и «Хочу» получало ноль. Экран при этом объявлял перерасход: план ноль, а
    траты есть.

    Собрать месячный запас за один месяц нельзя по определению — на это и
    нужен запас. Поэтому обязательным считается ровно то, что человек назвал
    своим взносом, и не больше остатка разрыва. Взнос не назван — подушка не
    исчезает: она остаётся первой в очереди распределения (`allocateIncome`,
    шаг 3) и получает деньги оттуда, а не из пола плана.
  */
  const bufferGap = bufferGapToThreshold(input.goals, monthlyNeed, 1);
  const bufferMonthly = minM(bufferGap, bufferContribution(input.goals));
  const mustFloor = add(minDebtPayments, sinkingFunds, bufferMonthly);

  if (minDebtPayments > ZERO) rationale.push(`Минимальные платежи по долгам ${fmt(minDebtPayments)}`);
  if (sinkingFunds > ZERO) rationale.push(`Резервы под известные будущие платежи ${fmt(sinkingFunds)}`);
  if (bufferMonthly > ZERO) {
    rationale.push(
      `Взнос в подушку ${fmt(bufferMonthly)} — базовая безопасность; `
      + `до одного месяца расходов не хватает ${fmt(bufferGap)}`,
    );
  } else if (bufferGap > ZERO) {
    rationale.push(
      `Подушке не хватает ${fmt(bufferGap)} до одного месяца расходов; `
      + 'взнос не задан, поэтому она наполняется из «Коплю» по очереди',
    );
  }

  // Шаг 3. Остаток на распределение.
  const surplus = clampPos(sub(income, add(needFloor, mustFloor)));
  const trapped = surplus < share(income, TRAP_THRESHOLD);
  if (trapped) {
    rationale.push(
      'Свободных денег почти не остаётся — сначала выход из долговой нагрузки, проценты подтянутся позже',
    );
  }

  // Шаг 4. Делим излишек по выбранному режиму.
  const split = MODE_SPLIT[input.mode];
  const [wantExtra, saveExtra] = distribute(surplus, [split.self, split.goals]) as [Money, Money];

  const amounts = {
    need: needFloor,
    want: wantExtra,
    save: add(mustFloor, saveExtra),
  };
  // Доли считаем от дохода, а не друг от друга: сумма может быть меньше 100%,
  // и «свободно» — честная четвёртая доля (SCREENS §15.1).
  const shares: PlanShares = {
    need: ratio(amounts.need, income),
    want: ratio(amounts.want, income),
    save: ratio(amounts.save, income),
  };

  return { income, needFloor, mustFloor, surplus, shares, amounts, trapped, rationale };
}

/** Свободная доля дохода: то, что не расписано ни по одной части плана. */
export function freeShare(p: PlanResult): number {
  return Math.max(0, 1 - p.shares.need - p.shares.want - p.shares.save);
}

const ratio = (part: Money, whole: Money): number =>
  whole <= ZERO ? 0 : Math.round((part / whole) * 1000) / 1000;

const fmt = (m: Money): string => `${Math.round(m / 100).toLocaleString('ru-RU')} ₽`;

/**
 * Три порога подушки (PRD §2.3). Подушка — единственная цель, которая меняет
 * приоритет по мере наполнения: один месяц расходов это разница между
 * «неприятность» и «катастрофа», а шестой — уже комфорт.
 */
export type BufferStage = 'critical' | 'base' | 'target' | 'done';

export const BUFFER_MONTHS: Record<Exclude<BufferStage, 'done'>, number> = {
  critical: 1,
  base: 3,
  target: 6,
};

export function bufferStage(current: Money, monthlyNeed: Money): BufferStage {
  if (monthlyNeed <= ZERO) return 'done';
  const months = current / monthlyNeed;
  if (months < BUFFER_MONTHS.critical) return 'critical';
  if (months < BUFFER_MONTHS.base) return 'base';
  if (months < BUFFER_MONTHS.target) return 'target';
  return 'done';
}

export function bufferMonths(current: Money, monthlyNeed: Money): number {
  if (monthlyNeed <= ZERO) return 0;
  return Math.round((current / monthlyNeed) * 10) / 10;
}

/**
 * Месячный взнос в подушку — тот, который назвал человек.
 *
 * Ноль означает «не назвал», а не «не нужно»: в этом случае подушка
 * наполняется из распределения по очереди, а не из обязательного пола плана.
 */
export function bufferContribution(goals: Goal[]): Money {
  const buffer = goals.find((g) => g.kind === 'emergency_fund' && g.status === 'active');
  return buffer ? buffer.monthlyContribution : ZERO;
}

/** Сколько не хватает подушке до указанного порога в месяцах. */
export function bufferGapToThreshold(
  goals: Goal[],
  monthlyNeed: Money,
  months: number,
): Money {
  const buffer = goals.find((g) => g.kind === 'emergency_fund' && g.status === 'active');
  if (!buffer) return ZERO;
  const target = share(monthlyNeed, months);
  return clampPos(sub(maxM(target, ZERO), buffer.currentAmount));
}
